// src/pages/SellerIntelligence/CatalogueHealth.js
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../SellerIntelligence.module.css';
import localStyles from './CatalogueHealth.module.css';
import { FiArrowLeft, FiLink, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import IssueResolutionPanel from './components/IssueResolutionPanel';
import { getProductCounts, getIssueAnalysis, initiateAmazonLogin } from './services/catalogueHealthApi';
import box1 from './assets/box1.png';
import box2 from './assets/box2.png';
import box3 from './assets/box3.png';
import box4 from './assets/box4.png';
import { AMAZON_MAINTENANCE_MODE, AMAZON_MAINTENANCE_TITLE, AMAZON_MAINTENANCE_MESSAGE } from '../../../config/amazonMaintenance';

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

const formatRevenue = (revenueEntry) => {
  if (!revenueEntry || revenueEntry.total_30d == null) return null;
  const symbol = CURRENCY_SYMBOLS[revenueEntry.currency] || '₹';
  const formatted = revenueEntry.total_30d >= 1000
    ? revenueEntry.total_30d.toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : revenueEntry.total_30d.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return `${symbol}${formatted}/mo`;
};

const formatInventoryAtRisk = (price, quantity, currency) => {
  if (price == null || quantity == null) return null;
  const value = price * quantity;
  if (value <= 0) return null;
  const symbol = CURRENCY_SYMBOLS[currency] || '₹';
  const formatted = value >= 1000
    ? value.toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return `${symbol}${formatted}`;
};

// Maintenance gate shown when Amazon integration is down. Sits outside the main
// CatalogueHealth component so hook order stays stable.
const CatalogueHealthMaintenanceScreen = () => {
  const navigate = useNavigate();
  return (
    <div className={styles.container}>
      <div style={{ padding: '64px 24px', textAlign: 'center', maxWidth: '560px', margin: '0 auto' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#2766AA', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '32px' }}
        >
          <FiArrowLeft /> Back to Dashboard
        </button>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1B2833', marginBottom: '12px' }}>{AMAZON_MAINTENANCE_TITLE}</h2>
        <p style={{ fontSize: '15px', lineHeight: 1.5, color: '#505050', margin: 0 }}>{AMAZON_MAINTENANCE_MESSAGE}</p>
      </div>
    </div>
  );
};

const CatalogueHealth = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [productCounts, setProductCounts] = useState(null);
  const [error, setError] = useState(null);
  const { connected: amazonConnected, loading: checkingAmazonStatus } = useAmazonStatus(authCtx.token);
  const [isConnecting, setIsConnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('product_list'); // 'product_list' | 'resolutions'
  const [selectedStatCard, setSelectedStatCard] = useState('total');
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelRow, setPanelRow] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [skuColumnShowsAsin, setSkuColumnShowsAsin] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState(null);
  const [refreshesRemaining, setRefreshesRemaining] = useState(3);
  const [retryAfterHours, setRetryAfterHours] = useState(null);
  const [sortOption, setSortOption] = useState('status');
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef(null);
  const filterRef = useRef(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    topSellers: null,
    brands: [],
    productTypes: [],
    fulfillmentTypes: [],
    errorCodes: [],
  });
  const [expandedResolutionRowId, setExpandedResolutionRowId] = useState(null);
  const [resolutionAnalysisByRowId, setResolutionAnalysisByRowId] = useState({});
  const [resolutionAnalysisLoadingRowId, setResolutionAnalysisLoadingRowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({ page: 1, per_page: 10, total_items: 0, total_pages: 1 });

  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortDropdownOpen]);

  useEffect(() => {
    if (!filterPanelOpen) return;
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterPanelOpen]);

  useEffect(() => {
    if (amazonConnected && authCtx.token) {
      fetchProductCounts(false, currentPage, rowsPerPage);
    }
  }, [amazonConnected, authCtx.token]);

  const handleConnectAmazon = async () => {
    if (!authCtx.token) return;
    setIsConnecting(true);
    try {
      const data = await initiateAmazonLogin(authCtx.token);
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (err) {
      console.error('Error initiating Amazon login:', err);
      setError('Failed to initiate Amazon connection. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const fetchProductCounts = async (forceRefresh = false, page = currentPage, perPage = rowsPerPage) => {
    setLoading(true);
    setError(null);
    setEmptyMessage(null);
    try {
      const result = await getProductCounts(authCtx.token, { page, perPage, forceRefresh });
      if (result.refreshes_remaining != null) setRefreshesRemaining(result.refreshes_remaining);
      if (result.retry_after_hours != null) setRetryAfterHours(result.retry_after_hours);
      setProductCounts(result.data);
      if (result.data?.pagination) setPaginationMeta(result.data.pagination);
      if (result.data?.last_updated) setLastUpdated(new Date(result.data.last_updated + 'Z'));
      if (result.data == null) setEmptyMessage(result.message || 'No catalogue health data yet. Click Fetch to load from Amazon.');
      setRetryAfterHours(null);
    } catch (err) {
      console.error('Error fetching product counts:', err);
      setError(err.message || 'Unable to connect to the server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromApi = () => {
    fetchProductCounts(true);
  };

  // Use API counts so Active + Suppressed + Out of Stock = Total (57)
  const total = productCounts?.total ?? 0;
  const active = productCounts?.active ?? 0;
  const suppressed = productCounts?.suppressed ?? 0;
  const outOfStock = productCounts?.out_of_stock ?? Math.max((productCounts?.inactive ?? 0) - (productCounts?.suppressed ?? 0), 0);

  const actionableFixes = productCounts?.suppression_analysis?.actionable_fixes ?? {};

  const tableRows = useMemo(() => {
    const rows = [];
    const suppressedItems = productCounts?.suppression_analysis?.suppressed_items ?? [];
    const warningItems = productCounts?.suppression_analysis?.warning_items ?? [];
    const activeItems = productCounts?.suppression_analysis?.active_items ?? [];
    const outOfStockItems = productCounts?.suppression_analysis?.out_of_stock_items ?? [];
    const revenueMap = productCounts?.revenue_per_asin ?? {};

    const outOfStockSkus = new Set(outOfStockItems.map((item) => item.sku).filter(Boolean));
    const warningItemsBySku = new Map();
    warningItems.forEach((item) => {
      if (item.sku) {
        warningItemsBySku.set(item.sku, item);
      }
    });

    suppressedItems.forEach((item, idx) => {
      const rev = revenueMap[item.asin];
      rows.push({
        id: `s-${idx}`,
        productName: item.title || 'Unknown Product',
        thumb: item.image_url || 'https://placehold.co/28x28',
        skuId: item.sku || '-',
        asin: item.asin || '-',
        status: 'Suppressed',
        fixesCount: item.issues?.length ?? 0,
        issues: item.issues ?? [],
        actionableFixes,
        listingScore: 0,
        revenueAtRisk: formatRevenue(rev) || '-',
        revenueAtRiskValue: rev?.daily_avg ?? 0,
        inventoryAtRisk: formatInventoryAtRisk(item.price, item.quantity, item.currency) || '-',
        inventoryAtRiskValue: (item.price != null && item.quantity != null) ? item.price * item.quantity : 0,
        fulfillmentType: item.fulfillment_type || null,
        itemPrice: item.price ?? null,
        itemQuantity: item.quantity ?? null,
        itemCurrency: item.currency ?? 'INR',
        brand: item.brand || '',
        productType: item.product_type || '',
        isAtRisk: true,
      });
    });

    warningItems.forEach((item, idx) => {
      // If this SKU is out of stock, we'll represent it in the Out of Stock rows instead.
      if (outOfStockSkus.has(item.sku)) {
        return;
      }
      const wRev = revenueMap[item.asin];
      rows.push({
        id: `w-${idx}`,
        productName: item.title || 'Unknown Product',
        thumb: item.image_url || 'https://placehold.co/28x28',
        skuId: item.sku || '-',
        asin: item.asin || '-',
        status: 'Warning',
        fixesCount: item.issues?.length ?? 0,
        issues: item.issues ?? [],
        actionableFixes,
        listingScore: 0,
        revenueAtRisk: formatRevenue(wRev) || '-',
        revenueAtRiskValue: wRev?.daily_avg ?? 0,
        inventoryAtRisk: formatInventoryAtRisk(item.price, item.quantity, item.currency) || '-',
        inventoryAtRiskValue: (item.price != null && item.quantity != null) ? item.price * item.quantity : 0,
        fulfillmentType: item.fulfillment_type || null,
        itemPrice: item.price ?? null,
        itemQuantity: item.quantity ?? null,
        itemCurrency: item.currency ?? 'INR',
        brand: item.brand || '',
        productType: item.product_type || '',
        isAtRisk: true,
      });
    });

    activeItems.forEach((item, idx) => {
      const aRev = revenueMap[item.asin];
      rows.push({
        id: `a-${idx}`,
        productName: item.title || 'Unknown Product',
        thumb: item.image_url || 'https://placehold.co/28x28',
        skuId: item.sku || '-',
        asin: item.asin || '-',
        status: 'Active Now',
        fixesCount: 0,
        issues: item.issues ?? [],
        actionableFixes,
        listingScore: 0,
        revenueAtRisk: formatRevenue(aRev) || '-',
        revenueAtRiskValue: aRev?.daily_avg ?? 0,
        inventoryAtRisk: formatInventoryAtRisk(item.price, item.quantity, item.currency) || '-',
        inventoryAtRiskValue: (item.price != null && item.quantity != null) ? item.price * item.quantity : 0,
        fulfillmentType: item.fulfillment_type || null,
        itemPrice: item.price ?? null,
        itemQuantity: item.quantity ?? null,
        itemCurrency: item.currency ?? 'INR',
        brand: item.brand || '',
        productType: item.product_type || '',
        isAtRisk: false,
      });
    });

    outOfStockItems.forEach((item, idx) => {
      const warningForSku = item.sku ? warningItemsBySku.get(item.sku) : null;
      const mergedIssues =
        (warningForSku?.issues && warningForSku.issues.length > 0)
          ? warningForSku.issues
          : (item.issues ?? []);
      const hasWarningStatus = !!warningForSku;

      const oRev = revenueMap[item.asin];
      rows.push({
        id: `o-${idx}`,
        productName: item.title || 'Unknown Product',
        thumb: item.image_url || 'https://placehold.co/28x28',
        skuId: item.sku || '-',
        asin: item.asin || '-',
        status: 'Out of Stock',
        hasWarningStatus,
        fixesCount: mergedIssues.length ?? 0,
        issues: mergedIssues,
        actionableFixes,
        listingScore: 0,
        revenueAtRisk: formatRevenue(oRev) || '-',
        revenueAtRiskValue: oRev?.daily_avg ?? 0,
        inventoryAtRisk: formatInventoryAtRisk(item.price, item.quantity, item.currency) || '-',
        inventoryAtRiskValue: (item.price != null && item.quantity != null) ? item.price * item.quantity : 0,
        fulfillmentType: item.fulfillment_type || null,
        itemPrice: item.price ?? null,
        itemQuantity: item.quantity ?? null,
        itemCurrency: item.currency ?? 'INR',
        brand: item.brand || '',
        productType: item.product_type || '',
        isAtRisk: true,
      });
    });

    return rows;
  }, [productCounts, actionableFixes]);

  const SORT_OPTIONS = [
    { key: 'product', label: 'Product' },
    { key: 'sku', label: 'SKU-ID' },
    { key: 'status', label: 'Status' },
    { key: 'listingScore', label: 'Listing Score' },
    { key: 'revenue', label: 'Revenue at Risk' },
    { key: 'inventory', label: 'Inventory at Risk' },
    { key: 'actions', label: 'Action Points' },
  ];

  const handleSortSelect = (key) => {
    if (sortOption === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortOption(key);
      setSortDirection('asc');
    }
    setSortDropdownOpen(false);
  };

  const filterOptions = useMemo(() => {
    const brands = [...new Set(tableRows.map(r => r.brand).filter(Boolean))].sort();
    const productTypes = [...new Set(tableRows.map(r => r.productType).filter(Boolean))].sort();
    const fulfillmentTypes = [...new Set(tableRows.map(r => r.fulfillmentType).filter(Boolean))].sort();
    const errorCodesMap = {};
    tableRows.forEach(r => {
      (r.issues || []).forEach(issue => {
        const code = issue.code;
        if (code && !errorCodesMap[code]) {
          errorCodesMap[code] = issue.message || code;
        }
      });
    });
    const errorCodes = Object.entries(errorCodesMap)
      .map(([code, message]) => ({ code, message }))
      .sort((a, b) => a.code.localeCompare(b.code));
    return { brands, productTypes, fulfillmentTypes, errorCodes };
  }, [tableRows]);

  const toggleFilter = (filterKey, value) => {
    setActiveFilters(prev => {
      const current = prev[filterKey];
      if (Array.isArray(current)) {
        return {
          ...prev,
          [filterKey]: current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value],
        };
      }
      return { ...prev, [filterKey]: prev[filterKey] === value ? null : value };
    });
  };

  const clearAllFilters = () => setActiveFilters({
    topSellers: null,
    brands: [],
    productTypes: [],
    fulfillmentTypes: [],
    errorCodes: [],
  });

  const activeFilterCount = [
    activeFilters.topSellers ? 1 : 0,
    activeFilters.brands.length,
    activeFilters.productTypes.length,
    activeFilters.fulfillmentTypes.length,
    activeFilters.errorCodes.length,
  ].reduce((a, b) => a + b, 0);

  const filteredRows = useMemo(() => {
    let rows = [...tableRows];

    if (selectedStatCard === 'active') {
      rows = rows.filter(r => r.status === 'Active Now' || r.status === 'Warning');
    } else if (selectedStatCard === 'suppressed') {
      rows = rows.filter(r => r.status === 'Suppressed');
    } else if (selectedStatCard === 'outOfStock') {
      rows = rows.filter(r => r.status === 'Out of Stock');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        r.productName.toLowerCase().includes(q) ||
        r.skuId.toLowerCase().includes(q) ||
        (r.asin && r.asin.toLowerCase().includes(q))
      );
    }

    // Top Sellers at Risk filter
    if (activeFilters.topSellers) {
      const sorted = [...rows].sort((a, b) =>
        activeFilters.topSellers === 'top10Value'
          ? b.inventoryAtRiskValue - a.inventoryAtRiskValue
          : (b.itemQuantity ?? 0) - (a.itemQuantity ?? 0)
      );
      const top10Ids = new Set(sorted.slice(0, 10).map(r => r.id));
      rows = rows.filter(r => top10Ids.has(r.id));
    }

    if (activeFilters.brands.length > 0) {
      rows = rows.filter(r => activeFilters.brands.includes(r.brand));
    }

    if (activeFilters.productTypes.length > 0) {
      rows = rows.filter(r => activeFilters.productTypes.includes(r.productType));
    }

    if (activeFilters.fulfillmentTypes.length > 0) {
      rows = rows.filter(r => activeFilters.fulfillmentTypes.includes(r.fulfillmentType));
    }

    if (activeFilters.errorCodes.length > 0) {
      rows = rows.filter(r =>
        r.issues && r.issues.some(issue => activeFilters.errorCodes.includes(issue.code))
      );
    }

    const statusOrder = { 'Suppressed': 0, 'Out of Stock': 1, 'Warning': 2, 'Active Now': 3 };
    const dir = sortDirection === 'asc' ? 1 : -1;

    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortOption) {
        case 'product':
          cmp = a.productName.localeCompare(b.productName);
          break;
        case 'sku':
          cmp = a.skuId.localeCompare(b.skuId);
          break;
        case 'status':
          cmp = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
          break;
        case 'listingScore':
          cmp = a.listingScore - b.listingScore;
          break;
        case 'revenue':
          cmp = a.revenueAtRiskValue - b.revenueAtRiskValue;
          break;
        case 'inventory':
          cmp = a.inventoryAtRiskValue - b.inventoryAtRiskValue;
          break;
        case 'actions':
          cmp = a.fixesCount - b.fixesCount;
          break;
        default:
          cmp = 0;
      }
      return cmp * dir;
    });

    return rows;
  }, [tableRows, selectedStatCard, searchQuery, sortOption, sortDirection, activeFilters]);

  // Re-fetch when page or rowsPerPage changes
  useEffect(() => {
    if (amazonConnected && authCtx.token) {
      fetchProductCounts(false, currentPage, rowsPerPage);
    }
  }, [currentPage, rowsPerPage]);

  const totalPages = paginationMeta.total_pages;
  const totalItems = paginationMeta.total_items;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const renderStatusBadge = (row) => {
    const status = row.status;

    // Out of stock + warning: show both badges stacked
    if (status === 'Out of Stock' && row.hasWarningStatus) {
      return (
        <div className={localStyles.statusBadgeStack}>
          <span className={localStyles.statusBadge + ' ' + localStyles.statusOutOfStock}>Out of Stock</span>
          <span className={localStyles.statusBadge + ' ' + localStyles.statusWarning}>Warning</span>
        </div>
      );
    }

    if (status === 'Suppressed') {
      return <span className={localStyles.statusBadge + ' ' + localStyles.statusSuppressed}>Suppressed</span>;
    }
    if (status === 'Out of Stock') {
      return <span className={localStyles.statusBadge + ' ' + localStyles.statusOutOfStock}>Out of Stock</span>;
    }
    if (status === 'Warning') {
      return <span className={localStyles.statusBadge + ' ' + localStyles.statusWarning}>Warning</span>;
    }
    return <span className={localStyles.statusBadge + ' ' + localStyles.statusActive}>Active Now</span>;
  };

  const getScoreBarClass = (score) => {
    if (score >= 8) return localStyles.scoreBarFill + ' ' + localStyles.high;
    if (score >= 6) return localStyles.scoreBarFill + ' ' + localStyles.mid;
    return localStyles.scoreBarFill + ' ' + localStyles.low;
  };

  if (checkingAmazonStatus) {
    return (
      <div className={styles.featureContainer}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate('/dashboard/seller-intelligence')}>
            <FiArrowLeft /> Back
          </button>
          <div className={styles.titleSection}>
            <div className={styles.iconTitle}>
              <h1>Catalogue Health</h1>
            </div>
            <p>Monitor and analyze the health of your product catalogue</p>
          </div>
        </div>
        <div className={styles.content}>
          <div style={{ textAlign: 'center', padding: '48px', color: '#737373' }}>Checking connection status...</div>
        </div>
      </div>
    );
  }

  if (!amazonConnected) {
    return (
      <div className={styles.featureContainer}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate('/dashboard/seller-intelligence')}>
            <FiArrowLeft /> Back
          </button>
          <div className={styles.titleSection}>
            <div className={styles.iconTitle}>
              <h1>Catalogue Health</h1>
            </div>
            <p>Monitor and analyze the health of your product catalogue</p>
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.amazonConnectModal}>
            <div className={styles.modalGraphic}>
              <div className={styles.graphicShapes}>
                <div className={styles.shape1}></div>
                <div className={styles.shape2}></div>
                <div className={styles.shape3}>
                  <div className={styles.amazonLogo}>
                    <span className={styles.amazonA}>a</span>
                  </div>
                </div>
                <div className={styles.shape4}></div>
                <div className={styles.iconFlow}>
                  <div className={styles.circle1}></div>
                  <div className={styles.waveLine}></div>
                  <div className={styles.circle2}></div>
                </div>
                <div className={styles.iconBox}></div>
                <div className={styles.iconChart}></div>
                <div className={styles.iconAlert}></div>
              </div>
            </div>
            <p className={styles.modalText}>Check Listings, Warning & Upgrade</p>
            <button className={styles.connectAmazonButton} onClick={handleConnectAmazon} disabled={isConnecting}>
              <FiLink className={styles.linkIcon} />
              {isConnecting ? 'Connecting...' : 'Connect Amazon'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.featureContainer}>
      <div className={localStyles.container}>
        {/* <button className={localStyles.backButton} onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button> */}

        <div className={localStyles.headerBlock}>
          <div className={localStyles.headerRow}>
            <div className={localStyles.headerLeft}>
              <div className={localStyles.titleRow}>
                <h1 className={localStyles.pageTitle}>Catalogue Health</h1>
                {lastUpdated && (
                  <span className={localStyles.lastUpdated}>
                    Last Updated: {lastUpdated.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}, {lastUpdated.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                )}
              </div>
              <div className={localStyles.pillBanner}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.7">
                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_166_75)"/>
                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_166_75)"/>
                </g>
                <defs>
                <linearGradient id="paint0_linear_166_75" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                <stop stopColor="#2766AA"/>
                <stop offset="1" stopColor="#0752B0"/>
                </linearGradient>
                <linearGradient id="paint1_linear_166_75" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                <stop stopColor="#2766AA"/>
                <stop offset="1" stopColor="#0752B0"/>
                </linearGradient>
                </defs>
                </svg>
                <span className={localStyles.pillText}>Real-time listing quality monitoring and automated resolution suggestions.</span>
              </div>
            </div>
            <div className={localStyles.headerRight}>
              {/* <button
                type="button"
                className={localStyles.fetchBtn}
                onClick={() => navigate('/dashboard/seller-intelligence/fixes')}
              >
                Fixes
              </button> */}
              <div className={localStyles.refreshBtnWrapper}>
                <button
                  type="button"
                  className={localStyles.refreshBtn}
                  onClick={handleFetchFromApi}
                  disabled={loading || refreshesRemaining <= 0}
                >
                  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.2738 14.5278C21.3637 17.35 19.3295 19.6717 16.6508 20.9441C13.9722 22.2174 10.8873 22.3289 8.12416 21.2517C5.36207 20.1755 3.16579 18.0061 2.0542 15.2566L1.6426 16.1827C1.50326 16.4925 1.1935 16.6919 0.85371 16.6887C0.735804 16.6865 0.618971 16.6608 0.510713 16.6115C0.302771 16.5193 0.139837 16.3489 0.0573252 16.1366C-0.0241381 15.9233 -0.0187763 15.6875 0.0734033 15.4796L1.39394 12.5128C1.48719 12.2995 1.66298 12.1334 1.88163 12.053C2.10029 11.9715 2.34147 11.9833 2.55155 12.0841L5.51834 13.4046C5.74557 13.4828 5.92993 13.6522 6.02641 13.873C6.12288 14.0938 6.12181 14.3446 6.02427 14.5643C5.92565 14.7841 5.74021 14.9534 5.51191 15.0295C5.2836 15.1056 5.03386 15.0821 4.82378 14.9652L3.59755 14.425C4.49255 16.7734 6.3233 18.6449 8.65141 19.5913C10.9806 20.5378 13.5969 20.4735 15.8769 19.4134C18.1556 18.3544 19.8921 16.395 20.6703 14.0048C20.7271 13.7743 20.8782 13.5782 21.0851 13.4624C21.2931 13.3466 21.5385 13.323 21.7647 13.3949C21.9909 13.4677 22.1763 13.6307 22.2781 13.845C22.3789 14.0605 22.3874 14.307 22.2996 14.5278L22.2738 14.5278ZM23.5085 5.36978C23.3006 5.2776 23.0648 5.27224 22.8515 5.35371C22.6393 5.43624 22.4689 5.59916 22.3767 5.80709L21.9651 6.72463C20.8514 3.97626 18.6542 1.80906 15.8907 0.73397C13.1275 -0.342191 10.0427 -0.230703 7.36407 1.04053C4.68548 2.31283 2.64992 4.6324 1.73672 7.45348C1.58988 7.91546 1.84498 8.40852 2.30695 8.55535C2.76892 8.70218 3.26199 8.44709 3.40882 7.98513C4.17842 5.58843 5.9127 3.62165 8.19377 2.55726C10.4747 1.49397 13.0964 1.42858 15.4266 2.37933C17.7579 3.33007 19.5865 5.20892 20.4731 7.5649L19.2554 7.01611C19.0475 6.92393 18.8117 6.91857 18.5984 7.00003C18.3862 7.08256 18.2157 7.24549 18.1235 7.45342C17.9306 7.8843 18.1225 8.39024 18.5523 8.58531L21.5191 9.90585C21.6316 9.94979 21.7506 9.97337 21.8706 9.97445C21.9757 9.97445 22.0797 9.95622 22.1793 9.923C22.3808 9.82117 22.5373 9.64646 22.6167 9.43424L23.9372 6.46745C24.1076 6.04619 23.9201 5.56378 23.5085 5.36978Z" fill="white"/>
                  </svg>
                  <div className={localStyles.refreshBtnLabel}>{loading ? 'Fetching…' : 'Refresh'}</div>
                </button>
                <div className={localStyles.remainingIndicator}>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={i < refreshesRemaining ? localStyles.remainingDotActive : localStyles.remainingDotInactive} />
                  ))}
                  <div className={localStyles.remainingText}>
                    {refreshesRemaining > 0
                      ? `${refreshesRemaining} Remaining for today`
                      : retryAfterHours != null && retryAfterHours > 0
                        ? `Limit reached. Next refresh in ${retryAfterHours}h (12:00 AM)`
                        : 'Limit reached. Resets at 12:00 AM'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 12px', background: '#fff4f3', borderRadius: 8, color: '#de524a', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            {error}
            <button type="button" onClick={() => fetchProductCounts(false)} style={{ marginLeft: 8, padding: '4px 12px', background: '#2766aa', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Retry</button>
          </div>
        )}
        {emptyMessage && !error && (
          <div className={localStyles.emptyBanner}>
            <span>{emptyMessage}</span>
            <button type="button" className={localStyles.fetchBtnSmall} onClick={handleFetchFromApi} disabled={loading}>
              {loading ? 'Fetching…' : 'Fetch'}
            </button>
          </div>
        )}
        <div className={localStyles.cardsRow + (loading ? ' ' + localStyles.loadingCards : '')}>
          <button
            type="button"
            className={localStyles.statCard + (selectedStatCard === 'total' ? ' ' + localStyles.selected : '')}
            onClick={() => setSelectedStatCard('total')}
            style={{
              backgroundColor: 'white',
              backgroundImage: `url(${box1})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 72%',
              backgroundSize: 'auto 100%',
            }}
          >
            <div className={localStyles.statCardContent}>
              <div className={localStyles.statLabel}>Total SKUs</div>
              <div className={localStyles.statValue}>{total}</div>
            </div>
          </button>
          <button
            type="button"
            className={localStyles.statCard + (selectedStatCard === 'active' ? ' ' + localStyles.selected : '')}
            onClick={() => setSelectedStatCard('active')}
            style={{
              backgroundColor: 'white',
              backgroundImage: `url(${box2})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 74%',
              backgroundSize: 'auto 100%',
            }}
          >
            <div className={localStyles.statCardContent}>
              <div className={localStyles.statLabel}>Active Listings</div>
              <div className={localStyles.statValue}>{active}</div>
            </div>
          </button>
          <button
            type="button"
            className={localStyles.statCard + (selectedStatCard === 'suppressed' ? ' ' + localStyles.selected : '')}
            onClick={() => setSelectedStatCard('suppressed')}
            style={{
              backgroundColor: 'white',
              backgroundImage: `url(${box3})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 72%',
              backgroundSize: 'auto 100%',
            }}
          >
            <div className={localStyles.statCardContent}>
              <div className={localStyles.statLabel}>Suppressed</div>
              <div className={localStyles.statValue}>{suppressed}</div>
            </div>
          </button>
          <button
            type="button"
            className={localStyles.statCard + (selectedStatCard === 'outOfStock' ? ' ' + localStyles.selected : '')}
            onClick={() => setSelectedStatCard('outOfStock')}
            style={{
              backgroundColor: 'white',
              backgroundImage: `url(${box4})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 72%',
              backgroundSize: 'auto 100%',
            }}
          >
            <div className={localStyles.statCardContent}>
              <div className={localStyles.statLabel}>Out of Stock</div>
              <div className={localStyles.statValue}>{outOfStock}</div>
            </div>
          </button>
        </div>
        
        <div className={localStyles.searchFiltersBlock}>
          <div className={localStyles.searchRow}>
            <div className={localStyles.searchInputWrap}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
              </svg>
              <input
                type="text"
                placeholder="Search for Project Names, ASIN and SKU-IDs"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {/* <div className={localStyles.searchViewToggle}>
              <button
                type="button"
                className={localStyles.searchViewToggleBtnActive}
              >
                Product List
              </button>
              <button
                type="button"
                className={localStyles.criticalIssuesBtn}
              >
                <div className={localStyles.criticalIssuesBadge}>1</div>
              <svg width="18" height="20" viewBox="0 0 29 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.4011 0.400653C13.5947 0.400653 12.7905 0.781565 12.3338 1.54452L0.720213 20.9382C-0.188521 22.4562 0.961922 24.4004 2.76776 24.4004L26.0369 24.3993C27.8428 24.3993 28.9932 22.4551 28.0845 20.9371L16.4709 1.54425C16.0142 0.782431 15.2065 0.400391 14.4001 0.400391L14.4011 0.400653ZM14.4011 1.22466C14.9078 1.22466 15.4168 1.47446 15.7154 1.97178L21.5222 11.6686L27.329 21.3655C27.9194 22.3523 27.2105 23.5515 26.0368 23.5515L2.76764 23.5504C1.59396 23.5504 0.885111 22.3511 1.47541 21.3644L13.0902 1.97152C13.3889 1.47417 13.8945 1.22466 14.4011 1.22466ZM14.3849 6.4444C13.4169 6.4444 12.6336 7.24127 12.6766 8.18168L13.0055 15.3592C13.0392 16.0871 13.6609 16.667 14.4104 16.667V16.6659H14.4162C15.16 16.6579 15.7735 16.0758 15.7991 15.3524L16.128 8.18183C16.171 7.24141 15.3877 6.44455 14.4197 6.44455L14.3849 6.4444ZM14.3849 7.29552H14.4209C14.902 7.29552 15.275 7.67644 15.2541 8.14439L14.9275 15.3219H14.9264L14.924 15.3242C14.9147 15.6 14.694 15.8102 14.4104 15.8147C14.1199 15.8147 13.891 15.6022 13.8782 15.3208L13.5516 8.14326C13.5307 7.67644 13.9038 7.29552 14.3849 7.29552ZM14.3523 17.621C13.3274 17.647 12.4953 18.471 12.4953 19.4747C12.4953 20.4931 13.3529 21.3284 14.4011 21.3284C15.4481 21.3284 16.3104 20.4942 16.3104 19.4747C16.3104 18.4563 15.4493 17.621 14.4011 17.621H14.3523ZM14.4011 18.4721C14.9763 18.4721 15.4354 18.9152 15.4354 19.4747C15.4354 20.0342 14.9763 20.4796 14.4011 20.4796C13.8259 20.4796 13.3704 20.0342 13.3704 19.4747C13.3704 18.9152 13.8259 18.4721 14.4011 18.4721Z" fill="#DE524A" stroke="#DE524A" stroke-width="0.8"/>
              </svg>
                <span className={localStyles.criticalIssuesLabel}>Critical Issues</span>
              </button>
            </div> */}
          </div>

          <div className={localStyles.filtersRow}>
            <div className={localStyles.filtersLeft} ref={filterRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className={localStyles.filterBtn + (activeFilterCount > 0 ? ' ' + localStyles.filterBtnActive : '')}
                onClick={() => setFilterPanelOpen(v => !v)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.6431 0H2.35692C1.05846 0 0 1.05846 0 2.35692C0 2.98462 0.246154 3.57538 0.689231 4.02462L5.53846 8.87385V14.5169C5.53846 15.3354 6.20308 16 7.02154 16C7.41538 16 7.79692 15.84 8.07385 15.5631L9.92 13.7169C10.2708 13.3662 10.4615 12.9046 10.4615 12.4123V8.86769L15.3108 4.01846C15.7538 3.57538 16 2.98462 16 2.35692C16 1.05846 14.9415 0 13.6431 0ZM14.4369 3.15077L9.59385 8C9.36 8.23385 9.23077 8.54154 9.23077 8.87385V12.4185C9.23077 12.5785 9.16308 12.7385 9.05231 12.8554L7.20615 14.7015C7.05846 14.8431 6.76923 14.72 6.76923 14.5169V8.87385C6.76923 8.54769 6.64 8.23385 6.40615 8.00615L1.56308 3.15077C1.35385 2.94154 1.23077 2.65231 1.23077 2.35692C1.23077 1.73538 1.73538 1.23077 2.35692 1.23077H13.6431C14.2646 1.23077 14.7692 1.73538 14.7692 2.35692C14.7692 2.65231 14.6462 2.94154 14.4369 3.15077Z" fill="#737373"/>
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className={localStyles.filterBadge}>{activeFilterCount}</span>
                )}
              </button>

              {filterPanelOpen && (
                <div className={localStyles.filterPanel}>
                  <div className={localStyles.filterPanelHeader}>
                    <span className={localStyles.filterPanelTitle}>Filters</span>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} className={localStyles.clearFiltersBtn}>
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Top Sellers at Risk */}
                  <div className={localStyles.filterSection}>
                    <div className={localStyles.filterSectionTitle}>Top Sellers at Risk</div>
                    {[
                      { value: 'top10Value', label: 'Top 10 by Revenue at Risk' },
                      { value: 'top10Qty', label: 'Top 10 by Inventory Quantity' },
                    ].map(opt => (
                      <label key={opt.value} className={localStyles.filterOption}>
                        <input
                          type="radio"
                          name="topSellers"
                          checked={activeFilters.topSellers === opt.value}
                          onChange={() => toggleFilter('topSellers', opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Brand */}
                  {filterOptions.brands.length > 0 && (
                    <div className={localStyles.filterSection}>
                      <div className={localStyles.filterSectionTitle}>Brand</div>
                      <div className={localStyles.filterOptionList}>
                        {filterOptions.brands.map(brand => (
                          <label key={brand} className={localStyles.filterOption}>
                            <input
                              type="checkbox"
                              checked={activeFilters.brands.includes(brand)}
                              onChange={() => toggleFilter('brands', brand)}
                            />
                            <span>{brand}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Category */}
                  {filterOptions.productTypes.length > 0 && (
                    <div className={localStyles.filterSection}>
                      <div className={localStyles.filterSectionTitle}>Category</div>
                      <div className={localStyles.filterOptionList}>
                        {filterOptions.productTypes.map(pt => (
                          <label key={pt} className={localStyles.filterOption}>
                            <input
                              type="checkbox"
                              checked={activeFilters.productTypes.includes(pt)}
                              onChange={() => toggleFilter('productTypes', pt)}
                            />
                            <span>{pt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mode of Fulfillment */}
                  {filterOptions.fulfillmentTypes.length > 0 && (
                    <div className={localStyles.filterSection}>
                      <div className={localStyles.filterSectionTitle}>Mode of Fulfillment</div>
                      {filterOptions.fulfillmentTypes.map(ft => (
                        <label key={ft} className={localStyles.filterOption}>
                          <input
                            type="checkbox"
                            checked={activeFilters.fulfillmentTypes.includes(ft)}
                            onChange={() => toggleFilter('fulfillmentTypes', ft)}
                          />
                          <span>{ft}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Filter by Error */}
                  {filterOptions.errorCodes.length > 0 && (
                    <div className={localStyles.filterSection}>
                      <div className={localStyles.filterSectionTitle}>Filter by Error</div>
                      <div className={localStyles.filterOptionList}>
                        {filterOptions.errorCodes.map(({ code, message }) => (
                          <label key={code} className={localStyles.filterOption}>
                            <input
                              type="checkbox"
                              checked={activeFilters.errorCodes.includes(code)}
                              onChange={() => toggleFilter('errorCodes', code)}
                            />
                            <span title={message}>
                              <strong>{code}</strong>
                              {message && message !== code ? ` — ${message.length > 45 ? message.slice(0, 45) + '…' : message}` : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={localStyles.sortWrapOuter} ref={sortRef}>
              <button
                type="button"
                className={localStyles.sortWrap}
                onClick={() => setSortDropdownOpen((v) => !v)}
              >
                <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M20.7733 11.2457C21.4378 10.5812 22.4471 11.5905 21.7826 12.2551L16.8374 17.2002C16.5753 17.4586 16.1264 17.4707 15.8402 17.1881L10.9081 12.2551C10.2436 11.5906 11.2529 10.5813 11.9174 11.2458L15.632 14.9604V0.714031C15.632 -0.225563 17.0596 -0.225563 17.0596 0.714031V14.9604L20.7733 11.2457ZM5.37079 0.0596984L5.16261 0.198175L0.217439 5.14239C-0.447071 5.8069 0.562233 6.81621 1.22674 6.1517L4.94139 2.43705V16.6835C4.94139 17.6231 6.36891 17.6231 6.36891 16.6835L6.36798 2.43705L10.0826 6.1517C10.7471 6.81621 11.7564 5.8069 11.0919 5.14239L6.15985 0.209357C5.9526 0.00582388 5.64587 -0.0601871 5.37079 0.0596984Z" fill="#737373"/>
                </svg>
                <span className={localStyles.sortLabel}>Sort By :</span>
                <span className={localStyles.sortValue}>
                  {SORT_OPTIONS.find((o) => o.key === sortOption)?.label}
                  {sortDirection === 'desc' ? ' ↓' : ' ↑'}
                </span>
                <FiChevronDown size={15} />
              </button>
              {sortDropdownOpen && (
                <div className={localStyles.sortDropdown}>
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={localStyles.sortDropdownItem + (sortOption === opt.key ? ' ' + localStyles.sortDropdownItemActive : '')}
                      onClick={() => handleSortSelect(opt.key)}
                    >
                      {opt.label}
                      {sortOption === opt.key && (
                        <span className={localStyles.sortArrow}>{sortDirection === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {viewMode === 'product_list' && (totalItems > 0 || filteredRows.length > 0) && (
            <div className={localStyles.paginationBar}>
              <div className={localStyles.paginationInfo}>
                Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalItems || filteredRows.length)} of {totalItems || filteredRows.length}
              </div>
              <div className={localStyles.paginationControls}>
                <select
                  className={localStyles.paginationSelect}
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[10, 20, 50].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <button type="button" className={localStyles.paginationBtn} disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
                <button type="button" className={localStyles.paginationBtn} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                {getPageNumbers().map(p => (
                  <button key={p} type="button" className={localStyles.paginationBtn + (p === currentPage ? ' ' + localStyles.paginationBtnActive : '')} onClick={() => setCurrentPage(p)}>{p}</button>
                ))}
                <button type="button" className={localStyles.paginationBtn} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                <button type="button" className={localStyles.paginationBtn} disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
              </div>
            </div>
          )}

          {viewMode === 'product_list' && (
        <div className={localStyles.tableSection}>
          <div className={localStyles.tableCard}>
            <div className={localStyles.tableWrap}>
              <table className={localStyles.table}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>
                      <button
                        type="button"
                        className={localStyles.skuIdHeaderBtn}
                        onClick={() => setSkuColumnShowsAsin((v) => !v)}
                        title={skuColumnShowsAsin ? 'Show SKU-IDs' : 'Show ASINs'}
                      >
                        <span>{skuColumnShowsAsin ? 'ASIN' : 'SKU-ID'}</span>
                        <FiChevronRight className={localStyles.skuIdHeaderArrow} aria-hidden />
                      </button>
                    </th>
                    <th>Status</th>
                    <th>Listing Score</th>
                    <th>Revenue at Risk</th>
                    <th>Inventory at Risk</th>
                    <th>Action Points</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#737373' }}>Loading items...</td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#737373' }}>No items found</td>
                    </tr>
                  ) : filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className={localStyles.productCell}>
                          <img src={row.thumb} alt="" className={localStyles.productThumb} />
                          <span className={localStyles.productName}>{row.productName}</span>
                          <div className={localStyles.productNameTooltip}>{row.productName}</div>
                        </div>
                      </td>
                      <td>
                        <span className={localStyles.skuBadge}>
                          {skuColumnShowsAsin ? (row.asin || '-') : row.skuId}
                        </span>
                      </td>
                      <td>{renderStatusBadge(row)}</td>
                      <td>
                        <div>
                          <div className={localStyles.listingScore}>{row.listingScore}/10</div>
                          <div className={localStyles.scoreBar}>
                            <div
                              className={getScoreBarClass(row.listingScore)}
                              style={{ width: `${(row.listingScore / 10) * 100}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={localStyles.revenueCell}>
                          {row.isAtRisk && row.revenueAtRisk !== '-' && (
                            <svg width="22" height="13" viewBox="0 0 22 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M20.7984 1.47649C21.5981 0.676797 20.3833 -0.53805 19.5836 0.261645L13.8755 5.96973L7.96737 3.83983C7.64575 3.72446 7.30051 3.8136 7.07416 4.03997L7.07153 4.03735L1.7192 9.38968L1.71832 7.07105C1.71832 5.94011 5.95228e-05 5.94011 5.95228e-05 7.07105V11.4638C5.95228e-05 11.9383 0.384625 12.3229 0.85918 12.3229H5.25189C6.38283 12.3229 6.38283 10.6046 5.25189 10.6046H2.93326L7.90277 5.6351L13.7348 7.73791C14.0529 7.88649 14.4445 7.82968 14.7067 7.56748L20.7984 1.47649Z" fill="#DE524A"/>
                            </svg>
                          )}
                          <span>{row.revenueAtRisk}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span>{row.inventoryAtRisk}</span>
                          {(row.itemQuantity != null || row.itemPrice != null) && (
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>
                              {row.itemQuantity != null ? `${row.itemQuantity} units` : 'qty?'}
                              {' × '}
                              {row.itemPrice != null
                                ? `${CURRENCY_SYMBOLS[row.itemCurrency] || '₹'}${row.itemPrice}`
                                : 'price?'}
                            </span>
                          )}
                          {row.fulfillmentType && (
                            <span style={{
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: '0.04em',
                              padding: '1px 5px',
                              borderRadius: 4,
                              alignSelf: 'flex-start',
                              backgroundColor: row.fulfillmentType === 'FBA' ? '#e8f4fd' : '#f0fdf4',
                              color: row.fulfillmentType === 'FBA' ? '#1a6fa8' : '#166534',
                              border: `1px solid ${row.fulfillmentType === 'FBA' ? '#93c5fd' : '#86efac'}`,
                            }}>
                              {row.fulfillmentType}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={localStyles.actionBtn + ' ' + localStyles.outline}
                          onClick={() => {
                            setPanelRow(row);
                            setPanelOpen(true);
                            setAnalysisData(null);
                            setAnalysisLoading(true);
                            getIssueAnalysis(authCtx.token, {
                              asin: row.asin,
                              sku: row.skuId,
                              product_title: row.productName,
                              issues: row.issues,
                            })
                              .then((result) => { if (result?.analyses) setAnalysisData(result.analyses); })
                              .catch((err) => console.error('Issue analysis error:', err))
                              .finally(() => setAnalysisLoading(false));
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="currentColor"/>
                          <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="currentColor"/>
                          </svg>
                          <span>{row.fixesCount} Fixes</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
          )}

          {viewMode === 'resolutions' && (
            <div className={localStyles.resolutionsSection}>
              <div className={localStyles.resolutionCard}>
                <div className={localStyles.resolutionCardHeader}>
                  <div className={localStyles.resolutionCardHeaderLeft}>
                    <div className={localStyles.resolutionChecklistIcon} aria-hidden>
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="0.5" y="0.5" width="35" height="35" rx="5.5" stroke="#78B3F3"/>
                      <path d="M15.4691 8.75635H30.0824C30.1492 8.76155 30.2164 8.75287 30.2798 8.73086C30.3431 8.70885 30.4012 8.67399 30.4504 8.62846C30.4996 8.58292 30.5389 8.52771 30.5657 8.46628C30.5926 8.40485 30.6064 8.33853 30.6064 8.27148C30.6064 8.20444 30.5926 8.13812 30.5657 8.07669C30.5389 8.01526 30.4996 7.96004 30.4504 7.91451C30.4012 7.86898 30.3431 7.83412 30.2798 7.81211C30.2164 7.7901 30.1492 7.78142 30.0824 7.78662H15.4691C15.347 7.79612 15.233 7.85132 15.1499 7.9412C15.0667 8.03109 15.0205 8.14903 15.0205 8.27148C15.0205 8.39394 15.0667 8.51188 15.1499 8.60177C15.233 8.69165 15.347 8.74685 15.4691 8.75635Z" fill="#78B3F3"/>
                      <path d="M15.4688 11.3789H30.082C30.2102 11.3789 30.3332 11.328 30.4238 11.2373C30.5145 11.1467 30.5654 11.0237 30.5654 10.8955C30.5654 10.7673 30.5145 10.6443 30.4238 10.5537C30.3332 10.463 30.2102 10.4121 30.082 10.4121H15.4688C15.3405 10.4121 15.2176 10.463 15.1269 10.5537C15.0363 10.6443 14.9854 10.7673 14.9854 10.8955C14.9854 11.0237 15.0363 11.1467 15.1269 11.2373C15.2176 11.328 15.3405 11.3789 15.4688 11.3789Z" fill="#78B3F3"/>
                      <path d="M30.0824 19.7925H15.4691C15.347 19.802 15.233 19.8572 15.1499 19.9471C15.0667 20.0369 15.0205 20.1549 15.0205 20.2773C15.0205 20.3998 15.0667 20.5177 15.1499 20.6076C15.233 20.6975 15.347 20.7527 15.4691 20.7622H30.0824C30.1492 20.7674 30.2164 20.7587 30.2798 20.7367C30.3431 20.7147 30.4012 20.6798 30.4504 20.6343C30.4996 20.5888 30.5389 20.5336 30.5657 20.4721C30.5926 20.4107 30.6064 20.3444 30.6064 20.2773C30.6064 20.2103 30.5926 20.144 30.5657 20.0825C30.5389 20.0211 30.4996 19.9659 30.4504 19.9204C30.4012 19.8748 30.3431 19.84 30.2798 19.818C30.2164 19.796 30.1492 19.7873 30.0824 19.7925Z" fill="#78B3F3"/>
                      <path d="M30.0824 27.9312H15.4691C15.347 27.9406 15.233 27.9959 15.1499 28.0857C15.0667 28.1756 15.0205 28.2936 15.0205 28.416C15.0205 28.5385 15.0667 28.6564 15.1499 28.7463C15.233 28.8362 15.347 28.8914 15.4691 28.9009H30.0824C30.1492 28.9061 30.2164 28.8974 30.2798 28.8754C30.3431 28.8534 30.4012 28.8185 30.4504 28.773C30.4996 28.7275 30.5389 28.6722 30.5657 28.6108C30.5926 28.5494 30.6064 28.4831 30.6064 28.416C30.6064 28.349 30.5926 28.2827 30.5657 28.2212C30.5389 28.1598 30.4996 28.1046 30.4504 28.059C30.4012 28.0135 30.3431 27.9786 30.2798 27.9566C30.2164 27.9346 30.1492 27.926 30.0824 27.9312Z" fill="#78B3F3"/>
                      <path d="M30.0824 16.8315H15.4691C15.347 16.841 15.233 16.8962 15.1499 16.9861C15.0667 17.076 15.0205 17.194 15.0205 17.3164C15.0205 17.4389 15.0667 17.5568 15.1499 17.6467C15.233 17.7366 15.347 17.7918 15.4691 17.8013H30.0824C30.1492 17.8065 30.2164 17.7978 30.2798 17.7758C30.3431 17.7538 30.4012 17.7189 30.4504 17.6734C30.4996 17.6278 30.5389 17.5726 30.5657 17.5112C30.5926 17.4498 30.6064 17.3834 30.6064 17.3164C30.6064 17.2494 30.5926 17.183 30.5657 17.1216C30.5389 17.0602 30.4996 17.005 30.4504 16.9594C30.4012 16.9139 30.3431 16.879 30.2798 16.857C30.2164 16.835 30.1492 16.8263 30.0824 16.8315Z" fill="#78B3F3"/>
                      <path d="M30.0824 25.3862H15.4691C15.347 25.3957 15.233 25.4509 15.1499 25.5408C15.0667 25.6307 15.0205 25.7486 15.0205 25.8711C15.0205 25.9935 15.0667 26.1115 15.1499 26.2014C15.233 26.2913 15.347 26.3465 15.4691 26.356H30.0824C30.1492 26.3612 30.2164 26.3525 30.2798 26.3305C30.3431 26.3085 30.4012 26.2736 30.4504 26.2281C30.4996 26.1825 30.5389 26.1273 30.5657 26.0659C30.5926 26.0045 30.6064 25.9381 30.6064 25.8711C30.6064 25.804 30.5926 25.7377 30.5657 25.6763C30.5389 25.6149 30.4996 25.5597 30.4504 25.5141C30.4012 25.4686 30.3431 25.4337 30.2798 25.4117C30.2164 25.3897 30.1492 25.381 30.0824 25.3862Z" fill="#78B3F3"/>
                      <path d="M6 12.5625H11.8125V6.75H6V12.5625ZM6.9668 7.7168H10.8428V11.5928H6.9668V7.7168Z" fill="#78B3F3"/>
                      <path d="M6 21.2812H11.8125V15.4688H6V21.2812ZM6.9668 16.4385H10.8428V20.3115H6.9668V16.4385Z" fill="#78B3F3"/>
                      <path d="M6 30H11.8125V24.1875H6V30ZM6.9668 25.1572H10.8428V29.0332H6.9668V25.1572Z" fill="#78B3F3"/>
                      </svg>
                    </div>
                    <div className={localStyles.resolutionTitleBlock}>
                      <div className={localStyles.resolutionTitle}>Title Optimization Required</div>
                      <div className={localStyles.resolutionSubtitle}>
                        {filteredRows.length} Product{filteredRows.length !== 1 ? 's' : ''} Affected
                      </div>
                    </div>
                    <div className={localStyles.resolutionHeaderDivider} aria-hidden />
                    <div className={localStyles.resolutionImpactBlock}>
                      <div className={localStyles.resolutionImpactLabel}>Combined Daily Impact</div>
                      <div className={localStyles.resolutionImpactValue}>
                        <svg width="22" height="13" viewBox="0 0 22 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M20.7979 1.47649C21.5976 0.676797 20.3828 -0.53805 19.5831 0.261645L13.875 5.96973L7.96689 3.83983C7.64526 3.72446 7.30003 3.8136 7.07367 4.03997L7.07104 4.03735L1.71871 9.38968L1.71783 7.07105C1.71783 5.94011 -0.000428758 5.94011 -0.000428758 7.07105V11.4638C-0.000428758 11.9383 0.384137 12.3229 0.858692 12.3229H5.2514C6.38234 12.3229 6.38234 10.6046 5.2514 10.6046H2.93277L7.90229 5.6351L13.7343 7.73791C14.0525 7.88649 14.444 7.82968 14.7062 7.56748L20.7979 1.47649Z" fill="#DE524A"/>
                        </svg>
                        <span className={localStyles.resolutionImpactText}>
                          ₹{filteredRows.reduce((sum, r) => sum + (r.revenueAtRiskValue || 0), 0).toLocaleString('en-IN')}/day
                        </span>
                      </div>
                    </div>
                  </div>
                  <button type="button" className={localStyles.resolveAllBtn}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="white"/>
                    <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="white"/>
                    </svg>
                    Resolve All
                  </button>
                </div>
                <div className={localStyles.resolutionCardBody}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#737373', fontSize: 14 }}>Loading items...</div>
                  ) : filteredRows.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#737373', fontSize: 14 }}>No items found. Adjust filters or fetch catalogue data.</div>
                  ) : filteredRows.map((row) => {
                    const isExpanded = expandedResolutionRowId === row.id;
                    const cachedAnalysis = resolutionAnalysisByRowId[row.id];
                    const isLoadingAnalysis = resolutionAnalysisLoadingRowId === row.id;
                    const handleWhyHowClick = () => {
                      if (isExpanded) {
                        setExpandedResolutionRowId(null);
                        return;
                      }
                      setExpandedResolutionRowId(row.id);
                      if (resolutionAnalysisByRowId[row.id] != null) return;
                      setResolutionAnalysisLoadingRowId(row.id);
                      getIssueAnalysis(authCtx.token, {
                        asin: row.asin,
                        sku: row.skuId,
                        product_title: row.productName,
                        issues: row.issues,
                      })
                        .then((result) => {
                          if (result?.analyses && Array.isArray(result.analyses)) {
                            setResolutionAnalysisByRowId((prev) => ({ ...prev, [row.id]: result.analyses }));
                          }
                        })
                        .catch((err) => console.error('Issue analysis error:', err))
                        .finally(() => setResolutionAnalysisLoadingRowId(null));
                    };
                    const firstAnalysis = cachedAnalysis && cachedAnalysis.length > 0 ? cachedAnalysis[0] : null;
                    const impactText = firstAnalysis?.impact_assessment || 'Current title may be too long or not optimized for mobile.';
                    const suggestedFixText = firstAnalysis?.suggested_fix || 'AI will optimize the title length and move key info to the front.';
                    return (
                      <div key={row.id} className={localStyles.resolutionRowWrap}>
                        <div className={localStyles.resolutionRow}>
                          <div className={localStyles.resolutionRowLeft}>
                            <div className={localStyles.resolutionProductThumb}>
                              <img src={row.thumb} alt="" />
                            </div>
                            <div className={localStyles.resolutionProductInfo}>
                              <div className={localStyles.resolutionProductTitle}>{row.productName}</div>
                              <div className={localStyles.resolutionProductAsin}>{row.asin || row.skuId}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className={localStyles.whyHowBtn + (isExpanded ? ' ' + localStyles.whyHowBtnExpanded : '')}
                            onClick={handleWhyHowClick}
                            aria-expanded={isExpanded}
                          >
                            Why & How?
                            {isExpanded ? (
                              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                <path d="M1.41 7.2002L6 2.74999L10.59 7.2002L12 5.83015L6 0.000195742L0 5.83015L1.41 7.2002Z" fill="#737373"/>
                              </svg>
                            ) : (
                              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                <path d="M1.41 0L6 4.4502L10.59 0L12 1.37004L6 7.2L0 1.37004L1.41 0Z" fill="#737373"/>
                              </svg>
                            )}
                          </button>
                        </div>
                        {isExpanded && (
                          <div className={localStyles.resolutionExpandBlock}>
                            {isLoadingAnalysis ? (
                              <div className={localStyles.resolutionExpandLoading}>Loading analysis…</div>
                            ) : (
                              <>
                                <div className={localStyles.resolutionExpandCol}>
                                  <div className={localStyles.resolutionExpandHeading}>Impact Analysis</div>
                                  <div className={localStyles.resolutionExpandText}>{impactText}</div>
                                </div>
                                <div className={localStyles.resolutionExpandDivider} aria-hidden />
                                <div className={localStyles.resolutionExpandCol}>
                                  <div className={localStyles.resolutionExpandHeading}>Suggested Fix (Recovers 35%)</div>
                                  <div className={localStyles.resolutionExpandText}>{suggestedFixText}</div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <IssueResolutionPanel
        isOpen={panelOpen}
        onClose={() => { setPanelOpen(false); setPanelRow(null); setAnalysisData(null); setAnalysisLoading(false); }}
        row={panelRow}
        analysisData={analysisData}
        analysisLoading={analysisLoading}
      />
    </div>
  );
};

// Route entry — short-circuits to the maintenance screen during the Amazon
// integration outage. Keeps CatalogueHealth's hook order untouched.
const CatalogueHealthRoute = () => (
  AMAZON_MAINTENANCE_MODE ? <CatalogueHealthMaintenanceScreen /> : <CatalogueHealth />
);

export default CatalogueHealthRoute;
